import React, { useCallback, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSetAtom } from "jotai";
import {
  DataGrid,
  GridActionsCellItem,
  type GridColDef,
  type GridRenderCellParams,
  gridClasses,
} from "@mui/x-data-grid";
import { Delete } from "@mui/icons-material";
import { Button } from "@mui/material";

import apiClient from "../../../utils/api-client";
import { PlainCard } from "../../../components/settings_components";
import { AsyncConfirmationModal } from "../../../components/asyncConfirmationModal";
import { customAlertStateAtom } from "../../../components/custom-alert";
import { trackSentryException } from "../../../utils/helpers";
import CreateApiKeyDialog, {
  createApiKeyDialogStateAtom,
} from "./CreateApiKeyDialog";

export type ApiKeyMetadata = {
  id: string;
  name: string;
  displayPrefix: string;
  createdAt: string | null;
  createdBy: string | null;
  lastUsedAt: string | null;
};

const formatDate = (value: string | null | undefined) => {
  if (!value) return "Never";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "N/A";
    return d.toLocaleString();
  } catch {
    return "N/A";
  }
};

const ApiKeysPanel = ({ companyId }: { companyId: string | undefined }) => {
  const queryClient = useQueryClient();
  const setCustomAlertState = useSetAtom(customAlertStateAtom);
  const setCreateDialogState = useSetAtom(createApiKeyDialogStateAtom);

  const [deleteKeyState, setDeleteKeyState] = useState<ApiKeyMetadata | null>(
    null
  );
  const resetDeleteState = useCallback(() => setDeleteKeyState(null), []);

  const apiKeysQuery = useQuery({
    queryKey: ["getApiKeys", companyId],
    queryFn: async () => {
      const response = await apiClient.get<ApiKeyMetadata[]>(
        "/settings/api-keys"
      );
      return response.data;
    },
    enabled: Boolean(companyId),
    refetchOnWindowFocus: false,
  });

  const deleteMutation = useMutation({
    mutationKey: ["deleteApiKey"],
    mutationFn: async () => {
      if (!deleteKeyState) return;
      try {
        await apiClient.delete(
          `/settings/api-keys/${encodeURIComponent(deleteKeyState.id)}`
        );
        await queryClient.invalidateQueries({
          queryKey: ["getApiKeys", companyId],
        });
        setCustomAlertState({
          open: true,
          type: "success",
          title: `API key "${deleteKeyState.name}" deleted`,
        });
      } catch (error: any) {
        trackSentryException(error);
        setCustomAlertState({
          open: true,
          type: "error",
          title: "Failed to delete API key",
          description:
            error?.response?.data?.detail ??
            (error instanceof Error ? error.message : String(error)),
        });
      }
      resetDeleteState();
    },
  });

  const columns: GridColDef[] = [
    { field: "name", headerName: "Name", flex: 1, minWidth: 200 },
    {
      field: "displayPrefix",
      headerName: "Prefix",
      flex: 1,
      minWidth: 220,
      renderCell: (params: GridRenderCellParams) => (
        <code className="font-mono text-xs">{String(params.value)}…</code>
      ),
    },
    {
      field: "createdAt",
      headerName: "Created",
      minWidth: 192,
      renderCell: (params: GridRenderCellParams) => (
        <span>{formatDate(params.value as string)}</span>
      ),
    },
    {
      field: "lastUsedAt",
      headerName: "Last Used",
      minWidth: 192,
      renderCell: (params: GridRenderCellParams) => (
        <span>{formatDate(params.value as string)}</span>
      ),
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      getActions: ({ row }) => [
        <GridActionsCellItem
          key="delete"
          icon={<Delete />}
          label="Delete"
          onClick={() => setDeleteKeyState(row as ApiKeyMetadata)}
          color="inherit"
        />,
      ],
    },
  ];

  const handleOpenCreateDialog = useCallback(() => {
    setCreateDialogState({ open: true });
  }, [setCreateDialogState]);

  return (
    <PlainCard>
      <CreateApiKeyDialog companyId={companyId} />
      <AsyncConfirmationModal
        title={"Delete this API key?"}
        actionTitle={"Delete"}
        backTitle={"Cancel"}
        closeHandler={resetDeleteState}
        description={
          deleteKeyState
            ? `"${deleteKeyState.name}" (${deleteKeyState.displayPrefix}…) will be revoked immediately. Any CI pipelines or integrations using it will stop working.`
            : ""
        }
        open={deleteKeyState != null}
        actionHandler={deleteMutation.mutateAsync}
        actionIcon={<Delete />}
        submitting={deleteMutation.isPending}
      />
      <div className="grid grid-cols-2">
        <h1 className="text-3xl pl-4 pb-2 font-bold">API Keys</h1>
        <div className="place-self-end mt-2 pr-2">
          <Button
            variant="outlined"
            onClick={handleOpenCreateDialog}
            size="large"
          >
            + Create API Key
          </Button>
        </div>
      </div>
      <div className="pl-4 pr-4 pt-4 relative sm:rounded-lg overflow-auto h-[calc(100%-50.25px)]">
        <DataGrid
          showCellVerticalBorder={false}
          showColumnVerticalBorder={false}
          disableColumnMenu={true}
          className={"bg-white"}
          rows={apiKeysQuery.data ?? []}
          loading={apiKeysQuery.isLoading}
          columns={columns}
          getRowId={(row) => row.id}
          isRowSelectable={() => false}
          initialState={{
            sorting: { sortModel: [{ field: "createdAt", sort: "desc" }] },
          }}
          sx={{
            [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
              outline: "transparent",
            },
            [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
              { outline: "none" },
          }}
        />
      </div>
    </PlainCard>
  );
};

export { ApiKeysPanel };
export default ApiKeysPanel;
